// File: Dashboard.jsx
import React, { useEffect, useState } from "react";
import "../../Assets/css/Dashboard.css";
import { FaImages } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faStore,
  faCog,
  faSignOutAlt,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import user from "../../Assets/photo/user.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";
import DashboardTab from "./DashboardTab";
import PlanTab from "./PlanTab";
import LibraryTab from "./LibraryTab";
import MyUploadTab from "./MyUploadTab";
import SettingsTab from "./SettingsTab";
import LoadingImage from "../Common/LoadingImage";

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("dashboard"); // 'dashboard' | 'shop' | 'uploads' | 'settings'
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const activeTab = searchParams.get("tab");

  useEffect(() => {
    if (activeTab === "shop") {
      setActiveSection(activeTab);
    }
  }, []);

  // Mock user data (you can replace with API data)
  const [userData, setUserData] = useState({
    name: "Your Name",
    userName: "",
    profilePicture: user,
    plan: "free",
    status: "active",
    isProfileLinkDisable: false,
    stats: {
      totalViews: 0,
      uniqueVisitors: 0,
      bioscore: 0,
      viewsData: {
        7: [0, 0, 0, 0, 0, 0, 0],
        30: new Array(30).fill(0).map(() => 0),
        90: new Array(90).fill(0).map(() => 0),
      },
    },
    usedStorage: 5, // MB
    storageLimit: 20, // MB (free)
    planDetails: null,
  });

  useEffect(() => {
    const userInfo = localStorage.getItem("userInfo");
    const userDetails =
      userInfo && typeof userInfo === "string" ? JSON.parse(userInfo) : null;
    if (userDetails?.userId) {
      fetchUserData(userDetails?.userId);
      fetchProfileAnalytics();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.get(`/user/data`);
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          name: data.name || prev.name,
          userName: data?.userName || prev.userName,
          profilePicture: data.profileImage || prev.profilePicture,
          status: data?.status || false,
          isProfileLinkDisable: data?.isProfileLinkDisable || false,
          plan: data?.planType?.type || "free",
          planDetails: data?.planDetails || null,
        }));
      } else {
        console.log(
          "error ==>",
          response?.message || "user data fetch failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      console.log("error ==>", apiError);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileAnalytics = async () => {
    try {
      const response = await axiosUserInstance.get(
        `/profile-analytics/getAnalyticsWithTrend`
      );
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          stats: {
            totalViews: data?.totalViews || 0,
            uniqueVisitors: data?.uniqueVisitors || 0,
            bioscore: data?.bioScore || 0,
            viewsData: {
              7: data?.viewsData?.[7] || [0, 0, 0, 0, 0, 0, 0],
              30: data?.viewsData?.[30] || new Array(30).fill(0).map(() => 0),
              90: data?.viewsData?.[90] || new Array(90).fill(0).map(() => 0),
            },
          },
        }));
      } else {
        console.log(
          "error ==>",
          response?.message ||
            "profile-analytics fetach failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      console.log("error ==>", apiError);
    }
  };
  
  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <a href="/" className="logo">
          BioME.com
        </a>

        <div className="nav-links">
          <a href="/user/customprofile" className="nav-link">
            <FontAwesomeIcon icon={faUser} /> Profile
          </a>
          <button
            className="nav-link"
            onClick={() => {
              localStorage.removeItem("token");
              localStorage.removeItem("userInfo");
              navigate("/auth/login");
            }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="user-card">
            <LoadingImage
              src={userData?.profilePicture}
              alt="User Avatar"
              className="user-avatar"
              width="80px"
              height="80px"
              skeletonHeight="80px"
              style={{ borderRadius: "50%", objectFit: "cover" }}
            />
            <h3 className="user-name">{userData.name}</h3>
            <span className="user-plan plan-free text-capitalize">
              {/* {userData.plan === "free" ? "Free Plan" : "Premium Plan"} */}
              {userData?.plan} Plan
            </span>

            <div className="user-status">
              <span className={`status-dot status-${userData?.status}`}></span>
              <span style={{ textTransform: "capitalize" }}>
                {userData?.status}
              </span>
            </div>

            <button
              className="btn btn-outline"
              onClick={() => (window.location.href = "/user/customprofile")}
            >
              Edit Profile
            </button>
          </div>

          <ul className="sidebar-menu">
            <li
              className={`menu-item menu-link ${
                activeSection === "dashboard" ? "active" : ""
              }`}
              onClick={() => setActiveSection("dashboard")}
            >
              <FontAwesomeIcon icon={faChartLine} /> Dashboard
            </li>
            <li
              className={`menu-item menu-link ${
                activeSection === "shop" ? "active" : ""
              }`}
              onClick={() => setActiveSection("shop")}
            >
              <FontAwesomeIcon icon={faStore} /> Plans & Merchandise
            </li>
            {/* <li
              className={`menu-item menu-link ${
                activeSection === "uploads" ? "active" : ""
              }`}
              onClick={() => setActiveSection("uploads")}
            >
              <FontAwesomeIcon icon={faCloudUploadAlt} /> My Uploads
            </li> */}
            <li
              className={`menu-item menu-link ${
                activeSection === "settings" ? "active" : ""
              }`}
              onClick={() => setActiveSection("settings")}
            >
              <FontAwesomeIcon icon={faCog} /> Settings
            </li>
            <li
              className={`menu-item menu-link ${
                activeSection === "library" ? "active" : ""
              }`}
              onClick={() => setActiveSection("library")}
            >
              <FaImages size={20} /> Library
            </li>
          </ul>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Dashboard */}
          {activeSection === "dashboard" && (
            <DashboardTab userData={userData} />
          )}

          {/* Shop */}
          {activeSection === "shop" && <PlanTab userData={userData} />}

          {/* Uploads */}
          {activeSection === "uploads" && (
            <MyUploadTab userData={userData} setUserData={setUserData} />
          )}

          {/* Settings */}
          {activeSection === "settings" && (
            <SettingsTab userData={userData} setUserData={setUserData} setActiveSection={setActiveSection} />
          )}

          {activeSection === "library" && (
            <LibraryTab userData={userData} setUserData={setUserData} setActiveSection={setActiveSection} />
          )}
        </div>
      </div>

      {/* <button type="button" className="preview-profile-btn" onClick={() => navigate(`/${userData.userName}`)}>
        👁️ Preview
      </button> */}
    </div>
  );
}
